import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { User, MapPin, Package, X, Icon } from '@phosphor-icons/react';
import { AttachedEntity, EntityType } from '../../types';

// ═══════════════════════════════════════════════════════════════════════════════
// ENTITY CONFIG - Premiere Pro inspired muted colors
// ═══════════════════════════════════════════════════════════════════════════════
const ENTITY_CONFIG: Record<EntityType, { icon: Icon; color: string; bgColor: string; label: string }> = {
    subject: {
        icon: User,
        color: 'text-entity-soft',
        bgColor: 'bg-entity/10 border-entity/25',
        label: 'Character'
    },
    location: {
        icon: MapPin,
        color: 'text-place-soft',
        bgColor: 'bg-place/10 border-place/25',
        label: 'Location'
    },
    object: {
        icon: Package,
        color: 'text-thing-soft',
        bgColor: 'bg-thing/10 border-thing/25',
        label: 'Prop'
    }
};

interface EntityChipProps {
    entity: AttachedEntity;
    onRemove?: () => void;
    isDraggable?: boolean;
    showType?: boolean;
    size?: 'sm' | 'md';
}

export const EntityChip: React.FC<EntityChipProps> = ({
    entity,
    onRemove,
    isDraggable = false,
    showType = false,
    size = 'sm'
}) => {
    const config = ENTITY_CONFIG[entity.type];
    const Icon = config.icon;

    // Draggable setup
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging
    } = useDraggable({
        id: `entity-${entity.id}`,
        data: { type: 'entity', entity },
        disabled: !isDraggable
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1
    };

    // Premiere Pro style chip sizing
    const sizeClasses = size === 'sm'
        ? 'text-[10px] px-1.5 py-0.5 gap-1'
        : 'text-[11px] px-2 py-1 gap-1.5';

    const iconSize = size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3';

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...(isDraggable ? { ...attributes, ...listeners } : {})}
            className={`
                inline-flex items-center rounded border transition-all
                ${config.bgColor}
                ${sizeClasses}
                ${isDraggable ? 'cursor-grab active:cursor-grabbing hover:border-opacity-50' : ''}
                ${isDragging ? 'ring-1 ring-slate-blue/40' : ''}
            `}
        >
            <Icon className={`${iconSize} ${config.color}`} weight="fill" />

            {showType && (
                <span className={`${config.color} font-medium uppercase tracking-wide opacity-60`}>
                    {config.label}:
                </span>
            )}

            <span className="text-neutral-200 truncate max-w-[80px]">
                {entity.name}
            </span>

            {onRemove && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    className="ml-0.5 p-0.5 rounded text-gray-500 hover:text-neutral-200 hover:bg-iron/50 transition-colors"
                >
                    <X className="w-2.5 h-2.5" />
                </button>
            )}
        </div>
    );
};

// Draggable entity source (for Entity Dock in IdeaWorkspace)
// Accepts either entity prop OR individual props for backwards compatibility
interface DraggableEntityProps {
    entity?: AttachedEntity;
    // Legacy individual props (still supported)
    id?: string;
    type?: EntityType;
    name?: string;
    imageUrl?: string;
    refId?: string;
}

export const DraggableEntity: React.FC<DraggableEntityProps> = (props) => {
    // Support both entity prop and individual props
    const entity = props.entity || {
        id: `drag-${props.id}`,
        type: props.type!,
        ref_id: props.refId!,
        name: props.name!,
        image_url: props.imageUrl
    };

    const config = ENTITY_CONFIG[entity.type];
    const Icon = config.icon;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging
    } = useDraggable({
        id: `draggable-entity-${entity.id}`,
        data: { type: 'new-entity', entity }
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1
    };

    // Premiere Pro style draggable entity row
    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={`
                flex items-center gap-2 px-2 py-1.5 rounded
                bg-onyx border border-iron/40
                cursor-grab active:cursor-grabbing
                hover:bg-zinc-900 hover:border-iron
                transition-all group
                ${isDragging ? 'ring-1 ring-slate-blue/40 scale-[1.01] bg-zinc-900' : ''}
            `}
        >
            {/* Icon container with subtle color background */}
            <div className={`
                w-6 h-6 rounded flex items-center justify-center flex-shrink-0
                ${entity.type === 'subject' ? 'bg-entity/10' : ''}
                ${entity.type === 'location' ? 'bg-place/10' : ''}
                ${entity.type === 'object' ? 'bg-thing/10' : ''}
            `}>
                {entity.image_url ? (
                    <img
                        src={entity.image_url}
                        alt={entity.name}
                        className="w-4 h-4 rounded object-cover"
                    />
                ) : (
                    <Icon className={`w-3.5 h-3.5 ${config.color}`} weight="fill" />
                )}
            </div>

            {/* Name */}
            <span className="text-[11px] text-gray-400 truncate flex-1 font-medium group-hover:text-neutral-200">
                {entity.name}
            </span>

            {/* Drag indicator (subtle) */}
            <div className="w-3 h-3 rounded flex items-center justify-center opacity-0 group-hover:opacity-40 transition-opacity">
                <svg width="6" height="10" viewBox="0 0 6 10" fill="currentColor" className="text-gray-500">
                    <circle cx="1.5" cy="1.5" r="1"/>
                    <circle cx="4.5" cy="1.5" r="1"/>
                    <circle cx="1.5" cy="5" r="1"/>
                    <circle cx="4.5" cy="5" r="1"/>
                    <circle cx="1.5" cy="8.5" r="1"/>
                    <circle cx="4.5" cy="8.5" r="1"/>
                </svg>
            </div>
        </div>
    );
};

// Entity type filter chips
interface EntityTypeFilterProps {
    selectedTypes: EntityType[];
    onChange: (types: EntityType[]) => void;
}

// Premiere Pro style filter chips
export const EntityTypeFilter: React.FC<EntityTypeFilterProps> = ({
    selectedTypes,
    onChange
}) => {
    const toggleType = (type: EntityType) => {
        if (selectedTypes.includes(type)) {
            onChange(selectedTypes.filter(t => t !== type));
        } else {
            onChange([...selectedTypes, type]);
        }
    };

    return (
        <div className="flex items-center gap-1">
            {(Object.keys(ENTITY_CONFIG) as EntityType[]).map(type => {
                const config = ENTITY_CONFIG[type];
                const Icon = config.icon;
                const isSelected = selectedTypes.includes(type);

                return (
                    <button
                        key={type}
                        onClick={() => toggleType(type)}
                        className={`
                            flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium
                            transition-all border
                            ${isSelected
                                ? `${config.bgColor} ${config.color}`
                                : 'bg-onyx border-iron/40 text-gray-500 hover:text-gray-400 hover:border-iron'
                            }
                        `}
                    >
                        <Icon className="w-3 h-3" weight={isSelected ? 'fill' : 'regular'} />
                        {config.label}
                    </button>
                );
            })}
        </div>
    );
};
